/**
 * Modal: search invoices for the same customer and hard-link one to this order.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from "@mui/material";
import {
  getInvoiceLinkCandidates,
  linkInvoiceToOrder,
} from "../services/orderService.js";
import { logError } from "@/shared/utils/logger.js";
import { useAuth } from "@/contexts/AuthContext.jsx";

const reasonLabel = (code) => {
  if (!code) return "";
  const map = {
    cancelled: "Cancelled",
    invoice_settled_locked: "Settlement locked",
    already_linked_other_job: "Linked to another job",
  };
  return map[code] || code.replaceAll("_", " ");
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {function} props.onClose
 * @param {string} props.orderId
 * @param {function} [props.onLinked] — called after successful link
 */
const LinkInvoiceToOrderModal = ({ open, onClose, orderId, onLinked }) => {
  const { hasPermission } = useAuth();
  const canFinanceReconcile = hasPermission("finance:reconcile");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkingId, setLinkingId] = useState(null);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const fetchCandidates = useCallback(
    async (searchTerm) => {
      if (!orderId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await getInvoiceLinkCandidates(orderId, {
          q: searchTerm?.trim() || undefined,
          limit: 30,
          skip: 0,
        });
        if (!res.success) {
          setError(res.error || "Failed to load candidates");
          setPayload(null);
          return;
        }
        setPayload(res.data);
      } catch (err) {
        logError("LinkInvoiceToOrderModal load failed", { orderId, error: err?.message });
        setError(err?.message || "Failed to load candidates");
        setPayload(null);
      } finally {
        setLoading(false);
      }
    },
    [orderId]
  );

  useEffect(() => {
    if (open && orderId) {
      fetchCandidates("");
    }
  }, [open, orderId, fetchCandidates]);

  const handleSearch = () => {
    fetchCandidates(q);
  };

  const handleLink = async (invoiceId, candidate) => {
    setLinkingId(invoiceId);
    setError(null);
    try {
      const useForce =
        candidate?.requiresForceHardLink && canFinanceReconcile;
      const res = await linkInvoiceToOrder(orderId, {
        invoiceId,
        ...(useForce ? { forceHardLink: true } : {}),
      });
      if (!res.success) {
        setError(
          res.existingJobId
            ? `${res.error} (${res.existingJobId})`
            : res.error || "Failed to link invoice"
        );
        return;
      }
      if (onLinked) onLinked(res.data);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to link invoice");
    } finally {
      setLinkingId(null);
    }
  };

  const candidates = payload?.candidates || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Link existing invoice</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shows invoices for the same customer as this order. Linking sets the invoice job to this
          order and syncs the ledger.
        </Typography>
        <Box display="flex" gap={1} alignItems="flex-start" sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Search (invoice id, name, phone)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
          />
          <Button variant="outlined" onClick={handleSearch} disabled={loading}>
            Search
          </Button>
        </Box>
        {error && (
          <Typography color="error" variant="body2" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Eligible</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No invoices found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates.map((c) => (
                    <TableRow key={c.invoiceId}>
                      <TableCell>{c.invoiceId}</TableCell>
                      <TableCell>{String(c.status || "").replaceAll("_", " ")}</TableCell>
                      <TableCell align="right">
                        {Number(c.total || 0).toLocaleString("en-ZA", {
                          style: "currency",
                          currency: "ZAR",
                        })}
                      </TableCell>
                      <TableCell>
                        {c.eligible ? (
                          <Typography variant="body2" color="success.main">
                            {c.requiresForceHardLink
                              ? canFinanceReconcile
                                ? "Yes (finance reconcile)"
                                : "Yes (needs finance:reconcile)"
                              : "Yes"}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {reasonLabel(c.ineligibleReason)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          disabled={
                            !c.eligible ||
                            linkingId === c.invoiceId ||
                            (c.requiresForceHardLink && !canFinanceReconcile)
                          }
                          onClick={() => handleLink(c.invoiceId, c)}
                        >
                          {linkingId === c.invoiceId ? "Linking…" : "Link"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LinkInvoiceToOrderModal;
